import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { X, Award } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Language } from '../types';

interface StudentProgressProps {
  studentId: string;
  language: Language;
  onClose: () => void;
}

interface ProgressData {
  date: string;
  accuracy: number;
  classAccuracy: number;
  exercises: number;
  classExercises: number;
}

interface Badge {
  name: string;
  description: string;
  earned_at: string;
}

interface ActivityStats {
  multiplication: { accuracy: number, classAccuracy: number };
  division: { accuracy: number, classAccuracy: number };
  prime: { accuracy: number, classAccuracy: number };
  square: { accuracy: number, classAccuracy: number };
}

const StudentProgress: React.FC<StudentProgressProps> = ({ studentId, language, onClose }) => {
  const [progressData, setProgressData] = useState<ProgressData[]>([]);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);
  const [activityStats, setActivityStats] = useState<ActivityStats>({
    multiplication: { accuracy: 0, classAccuracy: 0 },
    division: { accuracy: 0, classAccuracy: 0 },
    prime: { accuracy: 0, classAccuracy: 0 },
    square: { accuracy: 0, classAccuracy: 0 }
  });

  useEffect(() => {
    loadStudentProgress();
    loadStudentBadges();
  }, [studentId]);

  const calculateAccuracy = (correct: number, total: number) => {
    return total > 0 ? Math.round((correct / total) * 100) : 0;
  };

  const loadStudentProgress = async () => {
    try {
      // Get the classroom ID for the current student
      const { data: classroomData } = await supabase
        .from('classroom_students')
        .select('classroom_id')
        .eq('student_id', studentId)
        .single();

      if (!classroomData) throw new Error('Student not found in any classroom');

      // Get all students in the same classroom
      const { data: classmates } = await supabase
        .from('classroom_students')
        .select('student_id')
        .eq('classroom_id', classroomData.classroom_id);

      if (!classmates) throw new Error('No classmates found');

      const classmateIds = classmates.map(c => c.student_id);

      // Get student data
      const { data: studentData, error: studentError } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', studentId)
        .order('created_at', { ascending: true });

      if (studentError) throw studentError;

      // Get class data for all students in the classroom
      const { data: classData, error: classError } = await supabase
        .from('user_progress')
        .select('*')
        .in('user_id', classmateIds)
        .order('created_at', { ascending: true });

      if (classError) throw classError;

      // Calculate activity stats
      const activities = ['multiplication', 'division', 'prime', 'square'] as const;
      const stats = {} as ActivityStats;

      for (const activity of activities) {
        // Student stats
        const studentActivity = studentData?.filter(d => d.operation === activity) || [];
        const studentCorrect = studentActivity.reduce((sum, d) => sum + d.correct_answers, 0);
        const studentTotal = studentActivity.reduce((sum, d) => sum + d.questions_answered, 0);
        const studentAccuracy = calculateAccuracy(studentCorrect, studentTotal);

        // Class stats (excluding current student)
        const classActivity = classData?.filter(d => d.operation === activity) || [];
        const classCorrect = classActivity.reduce((sum, d) => sum + d.correct_answers, 0);
        const classTotal = classActivity.reduce((sum, d) => sum + d.questions_answered, 0);
        const classAccuracy = calculateAccuracy(classCorrect, classTotal);

        stats[activity] = {
          accuracy: studentAccuracy,
          classAccuracy: classAccuracy
        };
      }

      setActivityStats(stats);

      // Group student data by date
      const studentGrouped = studentData?.reduce((acc: Record<string, { total_correct: number; total_questions: number }>, entry) => {
        const date = new Date(entry.created_at).toLocaleDateString();
        if (!acc[date]) {
          acc[date] = { total_correct: 0, total_questions: 0 };
        }
        acc[date].total_correct += entry.correct_answers;
        acc[date].total_questions += entry.questions_answered;
        return acc;
      }, {}) || {};

      // Group class data (excluding current student)
      const classGrouped = (classData || []).reduce((acc, entry) => {
        const date = new Date(entry.created_at).toLocaleDateString();
        if (!acc[date]) {
          acc[date] = { total_correct: 0, total_questions: 0 };
        }
        acc[date].total_correct += entry.correct_answers;
        acc[date].total_questions += entry.questions_answered;
        return acc;
      }, {} as Record<string, { total_correct: number; total_questions: number }>);

      // Combine data for charts
      const allDates = new Set([...Object.keys(studentGrouped), ...Object.keys(classGrouped)]);
      const formattedData = Array.from(allDates).map(date => {
        const studentStats = studentGrouped[date];
        const classStats = classGrouped[date];
        
        const studentAccuracy = studentStats?.total_questions > 0
          ? Math.round((studentStats.total_correct / studentStats.total_questions) * 100)
          : 0;

        const classAccuracy = classStats?.total_questions > 0
          ? Math.round((classStats.total_correct / classStats.total_questions) * 100)
          : 0;

        return {
          date,
          accuracy: studentAccuracy,
          classAccuracy: classAccuracy,
          exercises: studentStats?.total_questions || 0,
          classExercises: classStats?.total_questions || 0
        };
      });

      setProgressData(formattedData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()));
    } catch (error) {
      console.error('Error loading student progress:', error);
    }
  };

  const loadStudentBadges = async () => {
    try {
      const { data, error } = await supabase
        .from('user_badges')
        .select(`
          badge_id,
          earned_at,
          badges (
            name,
            description
          )
        `)
        .eq('user_id', studentId)
        .order('earned_at', { ascending: false });

      if (error) throw error;

      const formattedBadges = data.map(entry => ({
        name: entry.badges.name,
        description: entry.badges.description,
        earned_at: new Date(entry.earned_at).toLocaleDateString()
      }));

      setBadges(formattedBadges);
    } catch (error) {
      console.error('Error loading student badges:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-slate-800 rounded-xl p-6 max-w-4xl w-full mx-4">
          <div className="text-center py-8 text-slate-600 dark:text-slate-300">
            {language === 'en' ? 'Loading...' : 'Chargement...'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
            {language === 'en' ? 'Student Progress' : 'Progression de l\'élève'}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Activity Stats */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
            {language === 'en' ? 'Activity Performance' : 'Performance par activité'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(activityStats).map(([activity, stats]) => (
              <div key={activity} className="bg-slate-50 dark:bg-slate-700/50 p-4 rounded-lg">
                <h4 className="font-medium text-slate-700 dark:text-slate-200 mb-2">
                  {language === 'en' ? (
                    activity.charAt(0).toUpperCase() + activity.slice(1)
                  ) : (
                    activity === 'multiplication' ? 'Multiplication' :
                    activity === 'division' ? 'Division' :
                    activity === 'prime' ? 'Nombres premiers' :
                    'Nombres carrés'
                  )}
                </h4>
                <div className="flex flex-col gap-1">
                  <div className="text-sm">
                    <span className="text-indigo-600 dark:text-indigo-400 font-medium">{stats.accuracy}%</span>
                    <span className="text-slate-500 dark:text-slate-400 text-xs ml-1">
                      {language === 'en' ? 'accuracy' : 'précision'}
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="text-blue-400 dark:text-blue-300">{stats.classAccuracy}%</span>
                    <span className="text-slate-500 dark:text-slate-400 text-xs ml-1">
                      {language === 'en' ? 'class avg.' : 'moy. classe'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Daily Exercises */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
            {language === 'en' ? 'Daily Exercises' : 'Exercices quotidiens'}
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={progressData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: 'none',
                    borderRadius: '0.5rem',
                    color: '#f8fafc'
                  }}
                />
                <Legend />
                <Bar
                  dataKey="exercises"
                  fill="#4f46e5"
                  name={language === 'en' ? 'Student Exercises' : 'Exercices de l\'élève'}
                />
                <Bar
                  dataKey="classExercises"
                  fill="#93c5fd"
                  name={language === 'en' ? 'Class Average' : 'Moyenne de la classe'}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Daily Accuracy */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
            {language === 'en' ? 'Daily Accuracy' : 'Précision quotidienne'}
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={progressData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#94a3b8" />
                <YAxis domain={[0, 100]} stroke="#94a3b8" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: 'none',
                    borderRadius: '0.5rem',
                    color: '#f8fafc'
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="accuracy"
                  stroke="#4f46e5"
                  name={language === 'en' ? 'Student Accuracy' : 'Précision de l\'élève'}
                />
                <Line
                  type="monotone"
                  dataKey="classAccuracy"
                  stroke="#93c5fd"
                  name={language === 'en' ? 'Class Average' : 'Moyenne de la classe'}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Badges */}
        <div>
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
            {language === 'en' ? 'Earned Badges' : 'Badges obtenus'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {badges.map((badge, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-4 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg"
              >
                <Award className="h-6 w-6 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                <div>
                  <div className="font-medium text-indigo-900 dark:text-indigo-200">{badge.name}</div>
                  <div className="text-sm text-slate-600 dark:text-slate-300">{badge.description}</div>
                  <div className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
                    {language === 'en' ? 'Earned on ' : 'Obtenu le '}{badge.earned_at}
                  </div>
                </div>
              </div>
            ))}
            {badges.length === 0 && (
              <div className="col-span-2 text-center py-4 text-slate-600 dark:text-slate-400">
                {language === 'en' 
                  ? 'No badges earned yet'
                  : 'Pas encore de badges obtenus'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentProgress;